import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { createGetComponentByContentItem } from 'selectors/createGetComponent';
import { CONTENT_ITEM, LANGUAGES } from 'config/types/prop';
import { SINGLE } from 'config/types/content/component';
import { options } from 'config/reactRedux';
import { createGetContentItemBySlug } from 'selectors/createGetContentItem';
import { getRelatedContentItemsByItem } from 'selectors/relatedItemSelectors';
import { getContentType } from 'selectors/getMatch';
import { getEdition } from 'selectors/getEdition';
import Meta from 'components/meta';
import HeaderLinks from 'components/headerLinks';
import CampaignsTracker from 'components/tracking/campaignsTracker';
import withLoader from 'components/hoc/withLoader';
import Helmet from 'react-helmet';
import { isAbsolute } from 'utils/url';
import { getMetaImageDimensions } from 'utils/metadata';
import getIsInProductMagazine from 'selectors/getIsInProductMagazine';
import createGetEditionPath from 'selectors/createGetEditionPath';
import { setupLazyLoadEmbeds } from 'utils/lazyLoadEmbeds';
import removeLinks from 'utils/removeLinks';
import YoutubeLazyLoadEmbedStyles from 'components/youtube/youtubeLazyLoadEmbedsStyles';
import { actionArticleLinkClicked, actionArticleVideoStarted } from 'actions';
import { getIsActiveContentItemLoading, getIsActiveContentItemLoadingRelated } from 'selectors/getIsActiveContentItemLoading';

import ContentScriptEmbedHandler from './contentScriptEmbedHandler';
// import AbTester from 'components/abTester';

class ContentItem extends Component {
  constructor(props) {
    super(props);

    this.contentContainerRef = React.createRef();
    this.lazyLoadEmbedsTearDown = () => {};
    this.trackableLinksTearDown = () => {};
  }

  componentDidMount() {
    this.setupLazyLoadEmbeds();
    this.setupTrackableLinks();
  }

  componentDidUpdate() {
    this.setupLazyLoadEmbeds();
    this.setupTrackableLinks();
  }

  componentWillUnmount() {
    this.lazyLoadEmbedsTearDown();
    this.trackableLinksTearDown();
  }

  setupLazyLoadEmbeds() {
    this.lazyLoadEmbedsTearDown();

    const { contentLazyLoadEmbeds, onVideoStarted } = this.props;
    const container = this.contentContainerRef.current;
    if (Object.keys(contentLazyLoadEmbeds).length) {
      this.lazyLoadEmbedsTearDown =
        setupLazyLoadEmbeds(container, contentLazyLoadEmbeds, onVideoStarted).tearDown;
    }
  }

  setupTrackableLinks() {
    this.trackableLinksTearDown();

    const {
      createPath,
      isInProductMagazine,
      history,
      onLinkClicked,
    } = this.props;
    const container = this.contentContainerRef.current;
    const linksList = [...container.querySelectorAll('a')];

    const inProductNavigation = (event) => {
      const link = createPath(event.currentTarget.href);
      if (!isAbsolute(link)) {
        event.preventDefault();
        history.push(link);
      } else {
        event.preventDefault();
      }
    };

    const trackClickEvent = (event) => {
      const isTargetAnAnchorTag = 'a' === event.target.localName;
      // Older articles wrap their anchor text within span tags whenever there is a particular
      // style applied to the text. The following condition takes that into consideration
      const currentTarget = isTargetAnAnchorTag ? event.target : event.target.parentElement;
      const { href, innerText } = currentTarget;
      onLinkClicked({
        text: innerText,
        url: href,
      });
    };

    const clickEventListener = (event) => {
      trackClickEvent(event);
      if (isInProductMagazine) {
        inProductNavigation(event);
      }
    };

    const auxClickEventListener = (event) => {
      // tracks only mouse wheel clicks
      if (1 === event.button) {
        return clickEventListener(event);
      }

      return false;
    };

    linksList.forEach((linkElement) => {
      linkElement.addEventListener('click', clickEventListener);
      linkElement.addEventListener('auxclick', auxClickEventListener);
    });

    this.trackableLinksTearDown = () => {
      linksList.forEach((linkElement) => {
        linkElement.removeEventListener('click', clickEventListener);
        linkElement.removeEventListener('auxclick', auxClickEventListener);
      });
    };
  }

  buildImageMetaElements() {
    const metaList = this.props.item.metadata.headerMeta;
    const dimensions = getMetaImageDimensions(metaList);
    return dimensions && (
      <Helmet>
        <meta property="og:image:height" content={dimensions.height} />
        <meta property="og:image:width" content={dimensions.width} />
      </Helmet>
    );
  }

  render() {
    const {
      component: ContentItemComponent,
      contentFiltered,
      currentEdition,
      item,
      related,
      isInProductMagazine,
      contentLazyLoadEmbeds,
      isLoadingRelated,
    } = this.props;

    const {
      metadata: {
        canonicalUrl,
        coauthors: [author],
        headerMeta,
        headerTitle,
        languageHreflang,
        tracking: { shortlinkCode },
      },
    } = item;

    return (
      <div>
        {headerMeta && <Meta metaTags={headerMeta} title={headerTitle} />}
        {this.buildImageMetaElements()}
        <Helmet>
          <link rel="author" href={author.relativePath} />
        </Helmet>
        {/* {!isNode() && !abTester.wasQueried && <AbTester
          experimentGroup="test"
          experimentTarget="experiment_cro_353_mag_banner"
          magazineEditionLanguage={currentEdition}
        />} */}
        {0 < Object.keys(contentLazyLoadEmbeds).length && <YoutubeLazyLoadEmbedStyles />}
        <CampaignsTracker shortlinkCode={shortlinkCode} />
        <ContentScriptEmbedHandler contentScripts={this.props.contentScripts} />
        <HeaderLinks
          canonicalUrl={canonicalUrl}
          languageHreflang={languageHreflang}
        />
        <ContentItemComponent
          item={item}
          contentFiltered={contentFiltered}
          related={related}
          currentEdition={currentEdition}
          contentContainerRef={this.contentContainerRef}
          isInProductMagazine={isInProductMagazine}
          isLoadingRelated={isLoadingRelated}
        />
      </div>
    );
  }
}

ContentItem.propTypes = {
  // abTester: PropTypes.shape({
  //   isLoading: PropTypes.bool.isRequired,
  //   isInExperimentGroup: PropTypes.bool.isRequired,
  //   wasQueried: PropTypes.bool.isRequired,
  // }).isRequired,
  component: PropTypes.elementType.isRequired,
  contentLazyLoadEmbeds: PropTypes.object.isRequired,
  contentFiltered: PropTypes.string.isRequired,
  contentScripts: PropTypes.arrayOf(PropTypes.string).isRequired,
  createPath: PropTypes.func.isRequired,
  currentEdition: LANGUAGES.isRequired,
  item: PropTypes.shape(CONTENT_ITEM).isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
  onLinkClicked: PropTypes.func.isRequired,
  onVideoStarted: PropTypes.func.isRequired,
  related: PropTypes.object.isRequired,
  isInProductMagazine: PropTypes.bool.isRequired,
  isLoadingRelated: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, props) => {
  // const abTester = get('abTester', state);
  const contentType = getContentType(state);
  const getItem = createGetContentItemBySlug(contentType);
  const item = getItem(state, props);
  const getComponent = createGetComponentByContentItem(item, SINGLE);
  const component = getComponent(state);
  const loading = getIsActiveContentItemLoading(state) || !component;
  const related = getRelatedContentItemsByItem(state, item);
  const isInProductMagazine = getIsInProductMagazine(state);
  const { postProcessedData = {} } = item || {};
  const filterLinksIfNeeded = isInProductMagazine ? removeLinks : (content) => content;
  const contentFiltered = filterLinksIfNeeded(postProcessedData.filteredContent?.content || '');
  const createPath = (path) => createGetEditionPath(path)(state);

  return {
    component,
    contentLazyLoadEmbeds: postProcessedData.lazyLoadEmbeds,
    contentFiltered,
    contentScripts: postProcessedData.scripts,
    createPath,
    currentEdition: getEdition(state),
    item,
    loading,
    related,
    isInProductMagazine,
    isLoadingRelated: getIsActiveContentItemLoadingRelated(state),
  };
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onLinkClicked: actionArticleLinkClicked,
  onVideoStarted: actionArticleVideoStarted,
}, dispatch);

const withRedux = connect(
  mapStateToProps,
  mapDispatchToProps,
  undefined,
  options,
);

export default withRouter(withRedux(withLoader(ContentItem)));
